/**
 * Score All Notes (COM-390) run trigger + live status.
 *
 * Lets an admin kick off grading for every note in the current date range /
 * clinician filter that the clinician wrote entirely on their own (never run
 * through Comprehend's AI). Modeled on NoteQualityDashboard.js's common-edits
 * job pattern: start -> either a synchronous small-batch result or a jobId to
 * poll -> poll until completed/failed.
 *
 * Rendered by AnalyticsDashboard.js only when apiClient.features.score_all_notes
 * is on (this component also guards defensively in case it's ever rendered
 * without that check).
 *
 * Stays mounted across the filter-form / results switch because it owns the
 * running job's state and poll; unmounting it mid-run loses both. Past runs
 * are a separate component (ScoreAllRunsList) that belongs with the filters.
 */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Button, Spinner } from 'reactstrap';

// The status read is one row; a short run (a reused range, a small clinic)
// should not sit finished for a minute before its report opens.
const POLL_INTERVAL_MS = 15000;
const TERMINAL_STATUSES = ['completed', 'failed', 'cancelled'];

// What the runner is doing now (progress.phase, COM-564).
const PHASE_LEADS = {
  grading: 'Scoring notes in this range',
  export: 'Building the visit CSV',
  report: 'Building the report',
};

// The server's own reason when it gave one (JMPost keeps the message as
// 'Server Error' for its other callers).
const errorText = (error) =>
  error?.serverMessage || error?.message || 'Unable to start this run. Try again.';

// One banner shape for all four states, so a run reads as one thing changing
// rather than four unrelated lines. Colour carries the state; the left rule and
// the bold lead carry the structure, so it still parses without colour.
const BANNER_TONES = {
  info: { bg: '#f4f5ff', rule: '#5e72e4', lead: '#32325d' },
  success: { bg: '#f0fdf6', rule: '#2dce89', lead: '#1a7f52' },
  danger: { bg: '#fff5f7', rule: '#f5365c', lead: '#a61b3c' },
};

const StatusBanner = ({ tone = 'info', busy = false, lead, children }) => {
  const colors = BANNER_TONES[tone] || BANNER_TONES.info;
  return (
    <div
      role="status"
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px',
        background: colors.bg,
        borderLeft: `3px solid ${colors.rule}`,
        borderRadius: '6px',
        padding: '12px 16px',
        margin: '0 0 1rem 0',
      }}
    >
      {busy && (
        <Spinner
          size="sm"
          style={{ color: colors.rule, flex: '0 0 auto', marginTop: '2px' }}
        />
      )}
      <div style={{ minWidth: 0 }}>
        {lead && (
          <div
            style={{
              fontWeight: 600,
              fontSize: '0.875rem',
              color: colors.lead,
              lineHeight: 1.35,
            }}
          >
            {lead}
          </div>
        )}
        <div
          style={{
            fontSize: '0.8125rem',
            lineHeight: 1.45,
            color: '#6b7280',
            marginTop: lead ? '2px' : 0,
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

const ScoreAllNotesPanel = forwardRef(function ScoreAllNotesPanel(
  {
    apiClient,
    t1,
    t2,
    selectedLabels,
    selectedUserEmails,
    selectedRuleSet,
    noteSource,
    onViewRun,
    onRunComplete,
    onRunsChanged,
  },
  ref,
) {
  const [jobState, setJobState] = useState(null); // { jobId, status, progress }
  const [syncResult, setSyncResult] = useState(null); // direct (small-batch) result summary
  const [starting, setStarting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  // { kind: 'start' | 'failed', message }. A run that failed half way did
  // start, so it must not be reported as "didn't start".
  const [error, setError] = useState(null);
  const requestIdRef = useRef(0);
  const notifiedJobIdRef = useRef(null);


  const handleStart = useCallback(async () => {
    if (!apiClient?.features?.score_all_notes) return;
    const requestId = ++requestIdRef.current;
    notifiedJobIdRef.current = null;
    setJobState(null);
    setStarting(true);
    setError(null);
    setSyncResult(null);
    try {
      const response = await apiClient.startScoreAllNotes(
        t1.toISOString(),
        t2.toISOString(),
        selectedLabels,
        selectedUserEmails,
        true,
        selectedRuleSet,
        noteSource,
      );
      if (requestId !== requestIdRef.current) return;
      if (response?.error) {
        throw new Error(response.error);
      }
      if (response?.jobId) {
        // Async: a job was queued -- poll for status.
        setJobState({
          ...response,
          jobId: response.jobId,
          status: response.status || 'queued',
          progress: response.progress || null,
        });
      } else {
        // Synchronous: the backend graded a small batch (<=25 notes) inline
        // and returned a direct summary, with no job at all.
        setJobState(null);
        setSyncResult(response);
      }
    } catch (startError) {
      if (requestId !== requestIdRef.current) return;
      console.error('Unable to start score-all-notes run:', startError);
      setError({ kind: 'start', message: errorText(startError) });
    } finally {
      if (requestId === requestIdRef.current) setStarting(false);
    }
  }, [apiClient, t1, t2, selectedLabels, selectedUserEmails, selectedRuleSet, noteSource]);

  const handleCancel = useCallback(async () => {
    const jobId = jobState?.jobId;
    if (!jobId) return;
    setCancelling(true);
    try {
      const response = await apiClient.cancelScoreAllRun(jobId);
      if (response?.error) throw new Error(response.error);
      // Too late to cancel: it finished (or failed) first. Read the full
      // status so a completed run opens its report as usual.
      const next = response?.status === 'cancelled'
        ? { status: 'cancelled' }
        : await apiClient.getScoreAllStatus(jobId);
      setJobState((current) =>
        current?.jobId === jobId
          ? { ...current, ...next, jobId, progress: next.progress || current.progress }
          : current,
      );
      if (next.status === 'failed') {
        setError({ kind: 'failed', message: next.error || 'The run did not complete.' });
      }
      onRunsChanged?.();
    } catch (cancelError) {
      console.error('Unable to cancel score-all-notes run:', cancelError);
      setError({ kind: 'failed', message: `Couldn't cancel: ${errorText(cancelError)}` });
    } finally {
      setCancelling(false);
    }
  }, [apiClient, jobState?.jobId, onRunsChanged]);

  // Poll job status every 60s until it reaches a terminal status.
  useEffect(() => {
    const jobId = jobState?.jobId;
    const status = jobState?.status;
    if (!jobId || TERMINAL_STATUSES.includes(status)) {
      return undefined;
    }

    let cancelled = false;
    const requestId = requestIdRef.current;
    const poll = async () => {
      try {
        const response = await apiClient.getScoreAllStatus(jobId);
        if (cancelled || requestId !== requestIdRef.current) return;
        if (response?.error && !TERMINAL_STATUSES.includes(response.status)) {
          throw new Error(response.error);
        }
        setJobState({
          ...response,
          jobId,
          status: response.status,
          progress: response.progress || null,
        });
        if (response.status === 'failed') {
          setError({ kind: 'failed', message: response.error || 'The run did not complete.' });
        }
      } catch (pollError) {
        if (cancelled || requestId !== requestIdRef.current) return;
        console.error('Unable to refresh score-all-notes run status:', pollError);
      }
    };

    const interval = window.setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [apiClient, jobState?.jobId, jobState?.status]);

  // Fire the completion callback ONCE per job. The parent may pass a new
  // function identity on every render; without this latch the effect re-runs,
  // refetches analytics, re-renders, and loops -- it issued ~17k requests
  // before this guard existed.
  const lastStatus = jobState?.status;
  const lastJobId = jobState?.jobId;
  useEffect(() => {
    // Latch on 'completed' only: refresh_score_all_job can revive a stale
    // 'failed' job back to queued, and latching there would swallow the
    // refresh when it later completes.
    if (lastStatus !== 'completed') return;
    if (!lastJobId || notifiedJobIdRef.current === lastJobId) return;
    notifiedJobIdRef.current = lastJobId;
    // The report on screen was rendered before this run graded anything, so
    // it is stale the moment the run completes.
    onRunComplete?.(jobState);
  }, [lastStatus, lastJobId, jobState, onRunComplete]);

  // Generate Analytics starts the run; the button is gone.
  useImperativeHandle(ref, () => ({
    start: handleStart,
    // Follow a run started elsewhere (View building an old run's report)
    // exactly like one started here: poll, then onRunComplete.
    track: (run) => {
      requestIdRef.current += 1;
      notifiedJobIdRef.current = null;
      setSyncResult(null);
      setStarting(false);
      setError(null);
      setJobState({ ...run, status: run.status || 'queued', progress: run.progress || null });
    },
    reset: () => {
      requestIdRef.current += 1;
      setJobState(null);
      setSyncResult(null);
      setStarting(false);
      setError(null);
    },
  }), [handleStart]);

  // Feature-flag guard: this panel is meant to be rendered conditionally by
  // the parent, but degrade gracefully (render nothing) if that check is
  // ever missed or the flag flips off mid-session.
  if (!apiClient?.features?.score_all_notes) {
    return null;
  }

  const isRunInProgress = jobState && !TERMINAL_STATUSES.includes(jobState.status);
  const isCancelled = jobState?.status === 'cancelled';
  const progress = jobState?.progress || {};
  const phase = progress.phase || 'grading';
  const scoredSoFar = (progress.graded || 0) + (progress.skipped || 0) + (progress.failed || 0);

  // Mounted on every view, so render nothing at all when idle -- an empty div
  // with a top margin still pushes the page down.
  if (!starting && !error && !isRunInProgress && !syncResult && !isCancelled) {
    return null;
  }

  return (
    <div className="score-all-notes-panel">
      {starting && (
        <StatusBanner busy lead="Checking this range">
          Looking for notes that haven&apos;t been scored yet.
        </StatusBanner>
      )}

      {isRunInProgress && (
        <StatusBanner busy lead={PHASE_LEADS[phase] || PHASE_LEADS.grading}>
          {phase === 'grading' && progress.total > 0
            ? `${scoredSoFar.toLocaleString()} of ${progress.total.toLocaleString()} unscored notes checked. `
            : ''}
          The report opens here when the run finishes. You can leave this
          page; the run keeps going and appears under Recent exports.
          <div style={{ marginTop: '8px' }}>
            <Button
              type="button"
              size="sm"
              color="secondary"
              outline
              disabled={cancelling}
              onClick={handleCancel}
              data-testid="cancel-run"
            >
              {cancelling ? 'Cancelling...' : 'Cancel run'}
            </Button>
          </div>
        </StatusBanner>
      )}

      {isCancelled && (
        <StatusBanner lead="Run cancelled">
          Notes it already scored keep their scores. Generate Analytics to
          start again.
        </StatusBanner>
      )}

      {syncResult && !syncResult.total && (
        <StatusBanner lead="No clinicians match these filters">
          Choose other groups or clinicians, then Generate Analytics.
        </StatusBanner>
      )}

      {syncResult && !!syncResult.total && (
        <StatusBanner tone="success" lead="Scoring finished">
          {(syncResult.graded ?? 0).toLocaleString()} note
          {(syncResult.graded ?? 0) === 1 ? '' : 's'} scored
          {syncResult.failed
            ? `, ${syncResult.failed.toLocaleString()} couldn't be scored`
            : ''}
          .
        </StatusBanner>
      )}

      {error && (
        <StatusBanner
          tone="danger"
          lead={error.kind === 'start' ? "Scoring didn't start" : 'The run stopped'}
        >
          {error.message}
        </StatusBanner>
      )}
    </div>
  );
});

export default ScoreAllNotesPanel;
